import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { translations } from '@/utils/translations';

const EventCard = ({ event, nextEventDate, timezoneOffset = 0, timezoneId = 'UTC', language = 'de' }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const t = translations[language] || translations['de'];
  const months = t.months;

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      
      const targetYear = nextEventDate.getFullYear();
      const targetMonth = nextEventDate.getMonth();
      const targetDay = nextEventDate.getDate();
      
      const targetUTC = Date.UTC(targetYear, targetMonth, targetDay, 0, 0, 0);
      const targetInTimezone = targetUTC - (timezoneOffset * 3600000);
      
      const difference = targetInTimezone - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [nextEventDate, timezoneOffset]);

  const formatNumber = (num) => String(num).padStart(2, '0');
  
  const isUpcoming = timeLeft.days <= 30 && timeLeft.days > 0;

  return (
    <div 
      className="event-card glass-panel rounded-lg overflow-hidden target-lock group"
      style={{ 
        borderColor: `${event.color_primary}30`,
        '--tw-ring-color': event.color_primary 
      }}
      data-testid={`event-card-${event.id}`}
    >
      {/* Image Section */}
      <div className="relative h-32 overflow-hidden">
        {event.image_url ? (
          <img 
            src={event.image_url} 
            alt={event.name}
            className="event-image w-full h-full object-cover filter grayscale-[50%] group-hover:grayscale-0 transition-all duration-500"
          />
        ) : (
          <div 
            className="w-full h-full"
            style={{ 
              background: `linear-gradient(135deg, ${event.color_primary}20 0%, ${event.color_secondary}40 100%)` 
            }}
          />
        )}
        
        {/* Days indicator */}
        <div 
          className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-mono flex items-center gap-1"
          style={{ 
            backgroundColor: `${event.color_primary}20`,
            color: event.color_primary,
            border: `1px solid ${event.color_primary}40`
          }}
        >
          <Clock className="w-3 h-3" />
          {timeLeft.days > 0 ? `${timeLeft.days}${language === 'de' ? 'T' : 'D'}` : t.now}
        </div>

        {/* Upcoming badge */}
        {isUpcoming && (
          <div 
            className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-rajdhani uppercase tracking-wider animate-pulse"
            style={{ 
              backgroundColor: event.color_primary,
              color: '#020408'
            }}
          >
            {t.soon}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 
            className="font-rajdhani text-lg font-bold uppercase tracking-wide leading-tight"
            style={{ color: event.color_primary }}
          >
            {event.name}
          </h3>
        </div>

        <p className="font-mono text-xs text-slate-500 mb-3">
          {event.day}. {months[event.month - 1]} {event.year || nextEventDate.getFullYear()}
          {(event.end_day || event.end_month) && (
            <>
              {' - '}
              {event.end_day || event.day}. {months[(event.end_month || event.month) - 1]}
              {event.end_year ? ` ${event.end_year}` : (event.year ? ` ${event.year}` : ` ${nextEventDate.getFullYear()}`)}
            </>
          )}
        </p>

        <p className="font-barlow text-sm text-slate-400 leading-relaxed mb-4">
          {language === 'de' ? (event.description_de || event.description_en || event.description) : (event.description_en || event.description)}
        </p>

        {/* Mini Countdown */}
        <div 
          className="flex items-center justify-between p-2 rounded border"
          style={{ 
            backgroundColor: `${event.color_primary}05`,
            borderColor: `${event.color_primary}20`
          }}
        >
          <div className="flex flex-col">
            <span className="font-rajdhani text-xs uppercase tracking-wider text-slate-500">{t.countdown}</span>
            <span className="font-mono text-[10px] text-cyan-electric/70">{timezoneId}</span>
          </div>
          <div className="flex items-center gap-1 font-mono text-sm" style={{ color: event.color_primary }}>
            <span className="bg-black/30 px-1.5 py-0.5 rounded">{formatNumber(timeLeft.days)}</span>
            <span className="text-slate-600">:</span>
            <span className="bg-black/30 px-1.5 py-0.5 rounded">{formatNumber(timeLeft.hours)}</span>
            <span className="text-slate-600">:</span>
            <span className="bg-black/30 px-1.5 py-0.5 rounded">{formatNumber(timeLeft.minutes)}</span>
            <span className="text-slate-600">:</span>
            <span className="bg-black/30 px-1.5 py-0.5 rounded">{formatNumber(timeLeft.seconds)}</span>
          </div>
        </div>

        {/* Color indicator bar */}
        <div className="mt-3 h-1 rounded-full overflow-hidden flex">
          <div 
            className="flex-1"
            style={{ backgroundColor: event.color_primary }}
          />
          <div 
            className="flex-1"
            style={{ backgroundColor: event.color_secondary }}
          />
        </div>
      </div>
    </div>
  );
};

export default EventCard;

